import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/sl";
import type {
  CalendarShift,
  CalendarTimeOff,
  CalendarView,
} from "../../store/useCalendarStore";

/**
 * The span `GET /calendar/timeoff` is asked for. The month grid runs Monday to
 * Sunday past both edges of the month, as `Calendar/get.ts` builds it, so the
 * neighbouring months' days need their rows too.
 */
export function timeOffFetchWindow(
  selectedDate: Dayjs,
  view: CalendarView,
): { dateFrom: string; dateTo: string } {
  const date = selectedDate.locale("sl");
  const from =
    view === "month"
      ? date.startOf("month").startOf("week")
      : date.startOf(view);
  const to =
    view === "month" ? date.endOf("month").endOf("week") : date.endOf(view);

  return {
    dateFrom: from.format("YYYY-MM-DD HH:mm"),
    dateTo: to.format("YYYY-MM-DD HH:mm"),
  };
}

export type MonthDayAbsence = {
  userId: number;
  timeOffs: CalendarTimeOff[];
};

export type MonthTimedTimeOff = {
  timeOff: CalendarTimeOff;
  /** Local `YYYY-MM-DDTHH:mm`, clipped to the day. */
  start: string;
};

// `timeFrom`/`timeTo` and shift bounds are salon-local `YYYY-MM-DDTHH:mm`, so
// plain string comparison orders them.
function dayBounds(date: string) {
  return {
    dayStart: `${date}T00:00`,
    dayEnd: `${dayjs(date).add(1, "day").format("YYYY-MM-DD")}T00:00`,
  };
}

function overlapsDay(
  interval: { from: string; to: string },
  date: string,
): boolean {
  const { dayStart, dayEnd } = dayBounds(date);
  return interval.from < dayEnd && interval.to > dayStart;
}

function isDayLong(timeOff: CalendarTimeOff): boolean {
  return timeOff.source === "staff" && timeOff.typeGranularity === "DAY";
}

/**
 * What a month cell shows for `date`: whole-day absences grouped per person
 * (in `userIds` order) for the header badge, and everything else as timed
 * rows. Rows for staff or resources outside the filter are dropped.
 */
export function monthTimeOffsForDay({
  date,
  timeoffs,
  userIds,
  resourceIds,
}: {
  date: string;
  timeoffs: CalendarTimeOff[];
  userIds: number[];
  resourceIds: number[];
}): { absences: MonthDayAbsence[]; timed: MonthTimedTimeOff[] } {
  const { dayStart } = dayBounds(date);

  const onDay = timeoffs.filter((timeOff) => {
    const isShown =
      timeOff.source === "staff"
        ? timeOff.userId !== null && userIds.includes(timeOff.userId)
        : timeOff.resourceId !== null &&
          resourceIds.includes(timeOff.resourceId);

    return (
      isShown &&
      overlapsDay({ from: timeOff.timeFrom, to: timeOff.timeTo }, date)
    );
  });

  const absences = userIds.flatMap((userId) => {
    const timeOffs = onDay.filter(
      (timeOff) => isDayLong(timeOff) && timeOff.userId === userId,
    );
    return timeOffs.length > 0 ? [{ userId, timeOffs }] : [];
  });

  const timed = onDay
    .filter((timeOff) => !isDayLong(timeOff))
    .map((timeOff) => ({
      timeOff,
      start: timeOff.timeFrom > dayStart ? timeOff.timeFrom : dayStart,
    }))
    .sort((a, b) => a.start.localeCompare(b.start));

  return { absences, timed };
}

function isCoveredBy(
  segment: { from: string; to: string },
  intervals: { from: string; to: string }[],
): boolean {
  const sorted = [...intervals].sort((a, b) => a.from.localeCompare(b.from));
  let cursor = segment.from;

  for (const interval of sorted) {
    if (interval.from > cursor) break;
    if (interval.to > cursor) cursor = interval.to;
    if (cursor >= segment.to) return true;
  }

  return cursor >= segment.to;
}

/**
 * True when everyone in `userIds` who has a shift on `date` is away on
 * whole-day time off for all of that day's shift time. Nobody working is not
 * an absence, so a day without shifts is never marked.
 */
export function isDayFullyAbsent({
  date,
  shifts,
  timeoffs,
  userIds,
}: {
  date: string;
  shifts: CalendarShift[];
  timeoffs: CalendarTimeOff[];
  userIds: number[];
}): boolean {
  const { dayStart, dayEnd } = dayBounds(date);

  const segments = shifts
    .filter(
      (shift) =>
        userIds.includes(shift.userId) &&
        overlapsDay({ from: shift.start, to: shift.end }, date),
    )
    .map((shift) => ({
      userId: shift.userId,
      from: shift.start > dayStart ? shift.start : dayStart,
      to: shift.end < dayEnd ? shift.end : dayEnd,
    }));

  if (segments.length === 0) return false;

  return segments.every((segment) =>
    isCoveredBy(
      segment,
      timeoffs
        .filter(
          (timeOff) => isDayLong(timeOff) && timeOff.userId === segment.userId,
        )
        .map((timeOff) => ({ from: timeOff.timeFrom, to: timeOff.timeTo })),
    ),
  );
}
